import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

function AddCompliance() {
  const [formData, setFormData] = useState({
    trader_name: '',
    compliance_type: '',
    description: '',
  
    latitude: '',
    longitude: '',
    product_name: '',
    expiry_date: '',
    price_amount: '',
    license_number: '',
  });
  const [evidencePhotos, setEvidencePhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [locationDetected, setLocationDetected] = useState(false);

  // Axios instance with token interceptor
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
    timeout: 30000,
  });

  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Automatic location detection on page load
  useEffect(() => {
    const getLocationOnLoad = () => {
      if (!navigator.geolocation) {
        setLocationDetected(false);
        Swal.fire({
          icon: 'warning',
          title: 'Geolocation Not Supported',
          text: 'Your browser does not support geolocation.',
          confirmButtonText: 'OK',
        });
        return;
      }

      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
          }));
          setLocationDetected(true);
          setIsLocating(false);
          
          Swal.fire({
            icon: 'success',
            title: 'Location Detected!',
            html: `<div class="text-left">
                     <p><strong>Latitude:</strong> ${position.coords.latitude.toFixed(6)}</p>
                     <p><strong>Longitude:</strong> ${position.coords.longitude.toFixed(6)}</p>
                   </div>`,
            timer: 3000,
            showConfirmButton: false,
            position: 'top-end',
            toast: true,
          });
        },
        (error) => {
          setIsLocating(false);
          setLocationDetected(false);
          
          let errorMessage = 'Unable to retrieve your location.';
          if (error.code === 1) errorMessage = 'Location access denied. Please enable GPS.';
          if (error.code === 2) errorMessage = 'Location unavailable. Please check your GPS.';
          if (error.code === 3) errorMessage = 'Location request timed out.';
          
          Swal.fire({
            icon: 'warning',
            title: 'Location Access',
            text: errorMessage,
            confirmButtonText: 'OK',
          });
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    };

    setTimeout(() => {
      getLocationOnLoad();
    }, 500);
  }, []);

  // Manual location update
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      Swal.fire('Error', 'Geolocation is not supported', 'error');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString(),
        }));
        setLocationDetected(true);
        setIsLocating(false);
        
        Swal.fire({
          icon: 'success',
          title: 'Location Updated!',
          html: `<div class="text-left">
                   <p><strong>Latitude:</strong> ${position.coords.latitude.toFixed(6)}</p>
                   <p><strong>Longitude:</strong> ${position.coords.longitude.toFixed(6)}</p>
                 </div>`,
          timer: 2000,
          showConfirmButton: false,
          position: 'top-end',
          toast: true,
        });
      },
      (error) => {
        setIsLocating(false);
        
        let errorMessage = 'Unable to retrieve your location.';
        if (error.code === 1) errorMessage = 'Please enable location access in your browser settings.';
        
        Swal.fire({
          icon: 'error',
          title: 'Location Failed',
          text: errorMessage,
          confirmButtonText: 'Try Again',
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 10;
    const maxSize = 5 * 1024 * 1024;

    if (evidencePhotos.length + files.length > maxFiles) {
      Swal.fire('Warning', `You can only upload up to ${maxFiles} photos`, 'warning');
      return;
    }

    const validFiles = [];
    files.forEach(file => {
      if (file.size <= maxSize) {
        validFiles.push(file);
      } else {
        Swal.fire('Error', `${file.name} exceeds 5MB limit`, 'error');
      }
    });

    if (validFiles.length > 0) {
      setEvidencePhotos([...evidencePhotos, ...validFiles]);
    }
  };

  const removePhoto = (index) => {
    const newPhotos = [...evidencePhotos];
    newPhotos.splice(index, 1);
    setEvidencePhotos(newPhotos);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate location
    if (!formData.latitude || !formData.longitude) {
      Swal.fire({
        icon: 'warning',
        title: 'Location Required',
        text: 'Please enable location access to add a compliance record.',
        confirmButtonText: 'Get Location',
      }).then((result) => {
        if (result.isConfirmed) handleGetLocation();
      });
      return;
    }

    // Validate compliance type
    if (!formData.compliance_type) {
      Swal.fire('Warning', 'Please select compliance type', 'warning');
      return;
    }

    // Validate trader name
    if (!formData.trader_name) {
      Swal.fire('Warning', 'Please enter trader/business name', 'warning');
      return;
    }

    // Validate evidence photos
    if (evidencePhotos.length === 0) {
      Swal.fire('Warning', 'Please upload at least one evidence photo', 'warning');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      
      // Add evidence photos
      evidencePhotos.forEach((photo, index) => {
        submitData.append(`evidence_photos[${index}]`, photo);
      });

      const response = await api.post('/addcompliance', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Compliance record added successfully.',
          timer: 2000,
          showConfirmButton: false,
        });
        
        // Reset form
        setFormData({
          trader_name: '',
          compliance_type: '',
          description: '',
          location: '',
          latitude: '',
          longitude: '',
          product_name: '',
          expiry_date: '',
          price_amount: '',
          license_number: '',
        });
        setEvidencePhotos([]);
        setLocationDetected(false);
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response?.data?.message || 'Something went wrong!',
      });
    } finally {
      setLoading(false);
    }
  };

  // Reusable input classes
  const inputClasses = 'w-full bg-white border-2 border-teal-300 rounded-lg py-3 px-4 text-gray-700 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-300 hover:border-teal-400 transition duration-200';
  const disabledInputClasses = 'w-full bg-gray-100 border-2 border-gray-300 rounded-lg py-3 px-4 text-gray-500 cursor-not-allowed';
  const labelClasses = 'block text-gray-700 font-medium mb-1';

  return (
    <div className="max-w-3xl mx-auto p-6 bg-gray-50 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          Add Compliance Record
        </h2>
        <button
          type="button"
          onClick={handleGetLocation}
          disabled={isLocating}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm transition disabled:opacity-50"
        >
          {isLocating ? 'Detecting...' : '📍 Get Location'}
        </button>
      </div>

      {/* Location Display Fields */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-md font-semibold text-blue-800 mb-2">GPS Location (Auto-detected)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-blue-700 mb-1">Latitude</label>
            <input
              type="text"
              name="latitude"
              value={formData.latitude || (locationDetected ? 'Detected' : 'Not detected')}
              readOnly
              disabled
              className={disabledInputClasses}
              placeholder="Auto-detected latitude"
            />
          </div>
          <div>
            <label className="block text-xs text-blue-700 mb-1">Longitude</label>
            <input
              type="text"
              name="longitude"
              value={formData.longitude || (locationDetected ? 'Detected' : 'Not detected')}
              readOnly
              disabled
              className={disabledInputClasses}
              placeholder="Auto-detected longitude"
            />
          </div>
        </div>
        {!locationDetected && !formData.latitude && (
          <p className="text-xs text-red-600 mt-2">⚠ Click "Get Location" to detect your current GPS coordinates</p>
        )}
        {locationDetected && formData.latitude && (
          <p className="text-xs text-green-600 mt-2">✓ Location coordinates will be sent to the server</p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Hidden lat/lng fields that will be sent to backend */}
        <input type="hidden" name="latitude" value={formData.latitude} />
        <input type="hidden" name="longitude" value={formData.longitude} />

        {/* Compliance Type */}
        <div>
          <label className={labelClasses}>Compliance Type:</label>
          <select
            name="compliance_type"
            value={formData.compliance_type}
            onChange={handleChange}
            required
            className={inputClasses}
          >
            <option value="">Select type</option>
            <option value="expired_product">⚠️ Expired Product</option>
            <option value="price_violation">💰 Price Violation</option>
            <option value="no_license">📄 Trading Without License</option>
            <option value="short_weight">⚖️ Short Weight</option>
            <option value="counterfeit">🎭 Counterfeit Product</option>
            <option value="unsafe_product">⚠️ Unsafe Product</option>
            <option value="false_advertising">📢 False Advertising</option>
            <option value="tax_evasion">🏦 Tax Evasion</option>
          </select>
        </div>

        {/* Trader Name */}
        <div>
          <label className={labelClasses}>Trader/Business Name:</label>
          <input
            type="text"
            name="trader_name"
            value={formData.trader_name}
            onChange={handleChange}
            required
            className={inputClasses}
            placeholder="Enter trader or business name"
          />
        </div>

        {/* Description */}
        <div>
          <label className={labelClasses}>Description:</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows="4"
            className={inputClasses}
            placeholder="Describe the violation in detail..."
          />
        </div>

        {/* Specific Violation Details */}
        <div className="border-t border-gray-300 pt-4 mt-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Specific Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>Product Name (if applicable):</label>
              <input
                type="text"
                name="product_name"
                value={formData.product_name}
                onChange={handleChange}
                className={inputClasses}
                placeholder="e.g., Tomato, Cooking Oil"
              />
            </div>

            <div>
              <label className={labelClasses}>Expiry Date (if expired):</label>
              <input
                type="date"
                name="expiry_date"
                value={formData.expiry_date}
                onChange={handleChange}
                className={inputClasses}
              />
            </div>

            <div>
              <label className={labelClasses}>Price Amount (if price violation):</label>
              <input
                type="number"
                name="price_amount"
                value={formData.price_amount}
                onChange={handleChange}
                className={inputClasses}
                placeholder="e.g., 500.00 ETB"
                step="0.01"
              />
            </div>

            <div>
              <label className={labelClasses}>License Number (if unlicensed):</label>
              <input
                type="text"
                name="license_number"
                value={formData.license_number}
                onChange={handleChange}
                className={inputClasses}
                placeholder="Enter license number"
              />
            </div>
          </div>
        </div>

        {/* Location Address */}
        <div>
          <label className={labelClasses}>Location Address:</label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            className={inputClasses}
            placeholder="Enter detailed address"
          />
        </div>

        {/* Evidence Photos */}
        <div>
          <label className={labelClasses}>Evidence Photos (Required - Max 10):</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handlePhotoUpload}
            className="w-full border-2 border-teal-300 rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:border-cyan-500"
          />
          
          {evidencePhotos.length > 0 && (
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-2">{evidencePhotos.length} photo(s) selected:</p>
              <div className="flex flex-wrap gap-2">
                {evidencePhotos.map((photo, index) => (
                  <div key={index} className="relative inline-block bg-gray-100 rounded-lg px-3 py-1">
                    <span className="text-sm text-gray-700">
                      📷 {photo.name.substring(0, 25)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="ml-2 text-red-600 hover:text-red-800 font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Adding...' : 'Add Compliance'}
        </button>
      </form>
    </div>
  );
}

export default AddCompliance;